import { useFetcher, useSubmit } from "@remix-run/react";
import { useEffect, useState } from "react";
import { type NewConversationLoaderData } from "#app/routes/resources+/llm-chat.load-new.$excerptId";
import {
  type HydratedConversationContext,
  type ConversationLoaderData,
} from "#app/routes/resources+/llm-chat.load.$conversationId";
import { MarkType } from "#app/utils/domain/types";
import { cn } from "#app/utils/misc";
import { type ParsedMessage } from "../../../prisma/utils/llm-chat";
import ActionMenu from "../ui/action-menu";
import { Icon } from "../ui/icon";
import MarkChip from "./mark-chip";

const CHAT_MESSAGE_DISPLAY_STYLE =
  "rounded-md px-3 py-2 text-sm my-2 whitespace-pre-wrap";
const CHATBOX_DISPLAY_STYLE =
  "h-1/12 w-full rounded-md border border-input bg-background px-3 py-2.5 mt-1 text-sm dark:bg-slate-500 dark:text-white";

function ActionButton({
  text,
  onClick,
  className,
}: {
  text: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={cn(
        "rounded-md bg-slate-200 px-2 py-1 text-xs text-blue-800 hover:text-muted-foreground",
        className,
      )}
      onClick={onClick}
    >
      {text}
    </button>
  );
}

function CloseButton() {
  const fetcher = useFetcher();
  return (
    <Icon
      size="sm"
      name="cross-1"
      className="mx-0.5 cursor-pointer"
      onClick={() => {
        fetcher.submit(
          { open: false },
          {
            method: "post",
            action: "/resources/llm-chat/openclose",
          },
        );
      }}
    />
  );
}

function useChatbox() {
  const [enteredText, setEnteredText] = useState("");
  const fetcher = useFetcher();

  useEffect(() => {
    if (fetcher.data) {
      setEnteredText("");
    }
  }, [fetcher.data]);

  const submitChatMsg = (text: string) => {
    fetcher.submit(
      { message: text },
      { method: "post", action: "/resources/llm-chat/chat" },
    );
  };

  const handleUserClickEnter = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (enteredText.length > 0) {
        submitChatMsg(enteredText);
      }
    }
  };

  const loadingIndicator = (
    <div className="flex min-h-20 items-center justify-center">
      <Icon
        size="lg"
        name="dots-horizontal"
        className="mt-4 animate-caret-blink"
        title="loading"
      />
    </div>
  );

  const submitChatButton = (
    <Icon
      name="arrow-up"
      size="md"
      className="absolute bottom-4 right-2.5 cursor-pointer rounded-md bg-blue-700 p-0.5 text-white shadow-md"
      onClick={() => {
        submitChatMsg(enteredText);
      }}
    />
  );

  return {
    enteredText,
    setEnteredText,
    handleUserClickEnter,
    isLoadingChatResponse: fetcher.state === "submitting",
    loadingIndicator,
    submitChatButton,
  };
}

function FollowupChatBox() {
  const {
    enteredText,
    setEnteredText,
    handleUserClickEnter,
    isLoadingChatResponse,
    loadingIndicator,
    submitChatButton,
  } = useChatbox();

  const fetcher = useFetcher();

  return isLoadingChatResponse ? (
    loadingIndicator
  ) : (
    <div className="flex flex-col">
      <div className="relative mt-1 w-full">
        <textarea
          rows={6}
          className={cn(CHATBOX_DISPLAY_STYLE, "min-h-28")}
          placeholder="Send a followup or ask a new question!"
          value={enteredText}
          onChange={(e) => setEnteredText(e.target.value)}
          onKeyDown={handleUserClickEnter}
        />
        {submitChatButton}
      </div>
      <ActionButton
        text="Clear and start a new chat"
        onClick={() => {
          fetcher.submit(
            { switchToNewChat: true },
            { method: "post", action: "/resources/llm-chat/openclose" },
          );
        }}
        className="mx-1 mb-4 mt-4"
      />
    </div>
  );
}

const LlmMessage = ({
  text,
  primaryMediaId,
  conversationId,
}: {
  text: string;
  primaryMediaId: string;
  conversationId: string;
}) => {
  const submit = useSubmit();
  const onSaveAsNote = async () => {
    submit(
      {
        noteContent: text,
        mediaId: primaryMediaId,
        sourceConversationId: conversationId,
      },
      { method: "post", action: "/resources/llm-chat/save-as-note" },
    );
  };

  return (
    <div className="flex flex-row">
      <div className={cn(CHAT_MESSAGE_DISPLAY_STYLE)}>{text}</div>
      <ActionMenu
        triggerClassName="mt-3"
        menuItemContents={[
          // eslint-disable-next-line react/jsx-key
          <button type="submit" onClick={onSaveAsNote}>
            <Icon className="text-xs" name="pencil-2">
              Save as note
            </Icon>
          </button>,
        ]}
      />
    </div>
  );
};

const UserMessage = ({ text }: { text: string }) => {
  return (
    <div className={cn(CHAT_MESSAGE_DISPLAY_STYLE, "bg-slate-200")}>{text}</div>
  );
};

function ConversationContent({
  messages,
  primaryMediaId,
  conversationId,
}: {
  messages: ParsedMessage[];
  primaryMediaId: string;
  conversationId: string;
}) {
  return (
    <div className="mt-3">
      {messages.map((message, index) => (
        <div key={index} className="mb-1">
          {message.role === "assistant" ? (
            <LlmMessage
              text={message.content}
              primaryMediaId={primaryMediaId}
              conversationId={conversationId}
            />
          ) : (
            <UserMessage text={message.content} />
          )}
        </div>
      ))}
    </div>
  );
}

function isConversationData(data: any): data is ConversationLoaderData {
  return data && "messages" in data && "context" in data;
}

function primaryMediaId(
  conversationContext: HydratedConversationContext[],
): string {
  return conversationContext[0]!.excerpt.mediaId;
}

function ExistingConversationSidebar({
  conversationId,
}: {
  conversationId: string;
}) {
  const fetcher = useFetcher<ConversationLoaderData>();

  useEffect(() => {
    fetcher.load(`/resources/llm-chat/load/${conversationId}`);
  }, [conversationId]);

  const data = fetcher.data;
  return (
    <>
      {data && isConversationData(data) ? (
        <>
          {data.context.map((context) => (
            <MarkChip
              key={context.excerpt.id}
              id={context.excerpt.id}
              markType={MarkType.EXCERPT}
              textContent={context.excerpt.textContent}
              pageNumber={context.excerpt.pageNumber}
            />
          ))}
          <ConversationContent
            messages={data.messages}
            primaryMediaId={primaryMediaId(data.context)}
            conversationId={conversationId}
          />
          <FollowupChatBox />
        </>
      ) : (
        <Icon
          size="md"
          name="update"
          className="ml-2 animate-spin"
          title="loading"
        />
      )}
    </>
  );
}

function NewConversationChatBox() {
  const helpMeThinkFetcher = useFetcher();
  const isLoadingHelpMeThinkResponse =
    helpMeThinkFetcher.state === "submitting";

  const {
    enteredText,
    setEnteredText,
    handleUserClickEnter,
    isLoadingChatResponse,
    loadingIndicator,
    submitChatButton,
  } = useChatbox();

  if (isLoadingChatResponse || isLoadingHelpMeThinkResponse) {
    return loadingIndicator;
  }

  return (
    <div className="relative mt-3 w-full">
      <textarea
        rows={4}
        className={CHATBOX_DISPLAY_STYLE}
        placeholder="Ask me anything you'd like about this passage, or"
        value={enteredText}
        onChange={(e) => setEnteredText(e.target.value)}
        onKeyDown={handleUserClickEnter}
      />
      {submitChatButton}
      {enteredText.length === 0 && (
        <ActionButton
          text="🧠 Help me think about it!"
          onClick={() => {
            helpMeThinkFetcher.submit(
              { helpMeThink: true },
              { method: "post", action: "/resources/llm-chat/chat" },
            );
          }}
          className="absolute bottom-5 left-3"
        />
      )}
    </div>
  );
}

function NewConversationSidebar({ excerptId }: { excerptId: string }) {
  const fetcher = useFetcher<NewConversationLoaderData>();

  useEffect(() => {
    fetcher.load(`/resources/llm-chat/load-new/${excerptId}`);
  }, [excerptId]);

  const data = fetcher.data;

  return (
    <>
      {data ? (
        <>
          <MarkChip
            id={data.excerpt.id}
            markType={MarkType.EXCERPT}
            textContent={data.excerpt.textContent}
            pageNumber={data.excerpt.pageNumber}
          />
          <NewConversationChatBox />
        </>
      ) : (
        <Icon
          size="md"
          name="update"
          className="ml-2 animate-spin"
          title="loading"
        />
      )}
    </>
  );
}

export default function LLMSidebar({
  conversationId,
  excerptId,
}: {
  conversationId: string | null;
  excerptId: string;
}) {
  return (
    <div className="flex h-screen w-96 flex-col justify-start overflow-y-auto border-l border-slate-200 bg-gray-50 px-3 text-slate-700 shadow-md dark:bg-gray-300">
      <div className="sticky top-0 z-10 mb-3 flex flex-row justify-end bg-gray-50 pb-2 pt-4 dark:bg-gray-300">
        <CloseButton />
      </div>
      {conversationId ? (
        <ExistingConversationSidebar conversationId={conversationId} />
      ) : (
        <NewConversationSidebar excerptId={excerptId} />
      )}
    </div>
  );
}
